import { Box, Typography } from '@mui/material'

interface DimensionScore {
  key: string
  label: string
  score: number
}

interface MaturityRadarProps {
  scores: DimensionScore[]
  // Optional second polygon for previous scan — drawn faded behind the
  // current scan so users can see direction of change at a glance.
  previousScores?: DimensionScore[]
}

const MaturityRadar = ({ scores, previousScores }: MaturityRadarProps) => {
  const size = 280
  const cx = size / 2
  const cy = size / 2
  const maxRadius = 110
  const levels = [2, 4, 6, 8, 10]
  const n = scores.length

  const getPoint = (index: number, value: number) => {
    const angle = (Math.PI * 2 * index) / n - Math.PI / 2
    const r = (value / 10) * maxRadius
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) }
  }

  const gridPaths = levels.map(level => {
    const points = Array.from({ length: n }, (_, i) => getPoint(i, level))
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'
  })

  const dataPoints = scores.map((s, i) => getPoint(i, s.score))
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'

  const labelPoints = scores.map((s, i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2
    const r = maxRadius + 24
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) }
  })

  const scoreColor = (score: number) => {
    if (score >= 8) return '#4caf50'
    if (score >= 5) return '#ff9800'
    return '#f44336'
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <svg width={size + 80} height={size + 60} viewBox={`-40 -20 ${size + 80} ${size + 60}`}>
        {/* Grid levels */}
        {gridPaths.map((path, i) => (
          <path key={i} d={path} fill="none" stroke="#e0e0e0" strokeWidth={0.5} />
        ))}
        {/* Axes */}
        {scores.map((_, i) => {
          const p = getPoint(i, 10)
          return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e0e0e0" strokeWidth={0.5} />
        })}
        {/* Previous-scan polygon (faded grey) — drawn first so the
            current-scan polygon overlays it. Skipped when not provided
            or when the dimension order doesn't match. */}
        {previousScores && previousScores.length === n && (() => {
          const prevPoints = previousScores.map((s, i) => getPoint(i, s.score))
          const prevPath = prevPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'
          return <path d={prevPath} fill="rgba(120, 120, 120, 0.08)" stroke="#9e9e9e" strokeWidth={1} strokeDasharray="3 3" />
        })()}
        {/* Data polygon */}
        <path d={dataPath} fill="rgba(25, 118, 210, 0.2)" stroke="#1976d2" strokeWidth={2} />
        {/* Data points */}
        {dataPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={4} fill={scoreColor(scores[i].score)} stroke="#fff" strokeWidth={1.5} />
        ))}
        {/* Labels */}
        {labelPoints.map((p, i) => (
          <text
            key={i}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={10}
            fill="#666"
          >
            {scores[i].label}
          </text>
        ))}
        {/* Score values */}
        {dataPoints.map((p, i) => (
          <text
            key={`v${i}`}
            x={p.x}
            y={p.y - 10}
            textAnchor="middle"
            fontSize={9}
            fontWeight={600}
            fill={scoreColor(scores[i].score)}
          >
            {scores[i].score.toFixed(1)}
          </text>
        ))}
      </svg>
    </Box>
  )
}

export default MaturityRadar
